import React from 'react';
import { Header as HuiHeader, Icon } from '@digitools/honest-ui';
import { AccountCircleIcon } from '@digitools/honest-ui/icons';
import SwapHoriz from '@mui/icons-material/SwapHoriz';
import { parseJSON } from 'date-fns';
import { HeaderText } from './HeaderText';
import useAuthentication from '../../hooks/useAuthentication';
import useCustomerConfig from '../../hooks/useCustomerConfig';
import useExperience from '../../hooks/useExperience';
import useTranslate from '../../hooks/useTranslate';
import useTealium from '../../hooks/useTealium';
import { TEALIUM_EVENT } from 'src/constants/tealiumConstants';
import { Experience } from 'src/types/Experience';
import { getEnvVariable, isProd } from 'src/utils/envUtils';
import buildAccountMenuItems from './buildAccountMenuItems';
import buildExperienceMenuItems from './buildExperienceMenuItems';
import buildNavItems from './buildNavItems';
import { CustomEmployeeText } from 'src/utils/CustomExperienceText';
import NavMenuBar from '../NavMenuBar/NavMenuBar';
import './Header.css';
const Header = () => {
    const { isAuthenticated, user, logout, loading: authLoading } = useAuthentication();
    const { isCommPrefEnabled, mlpConfigs, isNewNav, setIsNewNav, navigationMenu } = useCustomerConfig();
    const { toggleLanguage, changeLanguage, setExperience, experience, language } = useExperience();
    const { t } = useTranslate();
    const { trackEvent } = useTealium();
    const customEmployeeText = CustomEmployeeText();
    // Wraps t() with the Custom-Employee substitution that MobileNav applies
    // inline as `CustomEmployeeText(t(label))`. Kept as a closure so buildNavItems
    // stays a pure function (no hook calls).
    const defaultEmployeeToken = t({ english: 'Employee', spanish: 'Empleado' });
    const translate = (label) => {
        const str = t(label);
        if (customEmployeeText === defaultEmployeeToken)
            return str;
        return str.replace(new RegExp(defaultEmployeeToken, 'g'), customEmployeeText);
    };
    const trackPageEvent = (eventName, version) => {
        trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.LINK,
            event_name: eventName,
            event_type: version,
            event_version: TEALIUM_EVENT.EVENT_TYPE.HEADER,
        });
    };
    // Leading '/' matters — Header renders these through react-router <Link>, which
    // resolves them against BrowserRouter's basename ('/customer').
    const publicSupportUrl = '/public/contacts';
    const authedSupportUrl = '/home/contacts';
    const onLanguageToggle = (nextLang) => {
        trackPageEvent('Language', nextLang);
        toggleLanguage();
    };
    /** Local testing - CLAUDE DO NOT DELETE THIS SECTION!! ---
     * Local login broken, using this section here to mock stuff out so i can see what a logged in user would look like from a public page
     * Changes made to testUtils to make it not use jest (doesn't exist when not running tests) so that file needs to be reverted later
     * mocks out user/account/nav stuff
     * To use uncomment this and comment out the prod code below this section
     */
    // const mockUser = getMockUserWithCustomerInContext();
    // const mockAccountMenu = buildAccountMenuItems( {user: mockUser, isCommPrefEnabled: true, updatedEmployerEmailNotificationsEnabled: true, lfgHomeUrl: '/', t: t, trackPageEvent: trackPageEvent} );
    // const mockExperienceMenu = buildExperienceMenuItems({
    //   user: mockUser,
    //   customEmployeeText,
    //   adminHomeUrl: 'https://admin.np-mylincolnportal.com',
    //   setExperience,
    //   setIsNewNav,
    //   changeLanguage,
    //   navigate,
    //   trackPageEvent,
    // });
    // const mockCurrentDashboardName = 'Employer Dashboard';
    // console.log('mock account', mockAccountMenu);
    // console.log('mock user:', mockUser)
    // console.log('mock experience menu', mockExperienceMenu);
    // return (
    //   <HuiHeader
    //     isLoggedIn={true}
    //     accountMenuLabel={`${t(HeaderText.welcome)} ${mockUser.firstName ?? ''}`}
    //     accountMenuIcon='person'
    //     accountMenuItems={mockAccountMenu}
    //       secondaryMenuLabel={`${t(HeaderText.viewing)}${mockCurrentDashboardName}`}
    //       secondaryMenuIcon='people-arrows'
    //       secondaryMenuItems={mockExperienceMenu}
    //       headerLabel={'Mock Testing Customer'}
    //     lastLoginDate={mockUser.lastLogin ? parseJSON(mockUser.lastLogin) : undefined}
    //     supportLabel={t(HeaderText.contactSupport)}
    //     supportUrl={authedSupportUrl}
    //     showToggleLanguage={true}
    //     onLanguageToggle={onLanguageToggle}
    //     onLogout={() => console.log('mock logout')}
    //   />
    // )
    /* End local testing section, prod code is below */
    const loggedOutHeader = (<>
      <HuiHeader isLoggedIn={false} dark language={language} supportLabel={t(HeaderText.contactSupport)} supportUrl={publicSupportUrl} showToggleLanguage={true} onLanguageToggle={onLanguageToggle}/>
      <NavMenuBar />
    </>);
    // Auth hasn't resolved yet: don't commit to the dark logged-out header (it would flash, then swap
    // to the light logged-in one). Render it hidden so its height is reserved without a hard-coded value.
    if (authLoading) {
        return (<div className='header-placeholder' aria-hidden='true'>
        {loggedOutHeader}
      </div>);
    }
    if (!isAuthenticated || !user) {
        return loggedOutHeader;
    }
    const accountMenuItems = buildAccountMenuItems({
        user,
        isCommPrefEnabled: !!isCommPrefEnabled,
        updatedEmployerEmailNotificationsEnabled: getEnvVariable('updatedEmployerEmailNotificationsEnabled'),
        lfgHomeUrl: getEnvVariable('lfgHomeUrl'),
        t,
        trackPageEvent,
    });
    const adminHomeUrl = isProd()
        ? 'https://admin.mylincolnportal.com'
        : 'https://admin.np-mylincolnportal.com';
    const experienceMenuItems = buildExperienceMenuItems({
        user,
        customEmployeeText,
        adminHomeUrl,
        setExperience,
        setIsNewNav,
        changeLanguage,
        trackPageEvent,
    });
    const navItems = buildNavItems({
        navigationMenu,
        user,
        translate,
        mlpHomeUrl: getEnvVariable('mlpHomeUrl'),
        trackPageEvent,
    });
    const showNewNavMenu = user.roles.includes('internal');
    const currentDashboardName = experience === Experience.EMPLOYEE
        ? `${customEmployeeText} ${showNewNavMenu ? (isNewNav ? 'Dashboard - New' : 'Dashboard - Legacy') : 'Dashboard'}`
        : experience === Experience.EMPLOYER
            ? 'Employer Dashboard'
            : '';
    const headerLabel = experience === Experience.EMPLOYER
        ? (mlpConfigs?.customerDisplayName || mlpConfigs?.customerInternalName)
        : undefined;
    const navHomeUrl = experience === Experience.EMPLOYER ? '/employer/home' : '/employee/home';
    return (<>
      <HuiHeader isLoggedIn={true} language={language} accountMenuLabel={`${t(HeaderText.welcome)} ${user.firstName ?? ''}`} accountMenuIcon={<AccountCircleIcon />} accountMenuItems={accountMenuItems} secondaryMenuLabel={experienceMenuItems ? `${t(HeaderText.viewing)}${currentDashboardName}` : undefined} secondaryMenuIcon={experienceMenuItems ? <Icon component={SwapHoriz}/> : undefined} secondaryMenuItems={experienceMenuItems} headerLabel={headerLabel} lastLoginDate={user.lastLogin ? parseJSON(user.lastLogin) : undefined} supportLabel={t(HeaderText.contactSupport)} supportUrl={authedSupportUrl} showToggleLanguage={experience !== Experience.EMPLOYER} onLanguageToggle={onLanguageToggle} onLogout={() => {
            trackPageEvent('Log Out', 'home page');
            logout();
        }} navItems={navItems} navHomeUrl={navHomeUrl}/>
      <NavMenuBar />
    </>);
};
export default Header;
